import React, { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { Search } from '@yme/assets/icons';
import { AutocompleteRequest } from '@yme/atoms/autocomplete-request';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { getRootLang } from '@yme/selectors/common';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { DropdownListMaxHeight, MIN_SEARCH_LENGTH } from './constants';
import { findProductByKey, getIsEndOfScroll, prepareProductsInRightFormat } from './helpers';
import { getProductByName } from './services';
export function SearchByName(props) {
    const { labels, productTypeId, productsByQueryEnd, setSelectedProduct, isRightToLeft, resultsListRangeOfMaxVisibleItems, } = props;
    const [searchText, setSearchText] = useState('');
    const [selectModelScrollHeight, setSelectModelScrollHeight] = useState(0);
    const [productOffsetParam, setProductOffsetParam] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [productResult, setProductResult] = useState([]);
    const [currentProductResult, setCurrentProductResult] = useState(null);
    const locale = useSelector(getRootLang);
    const { isInfiniteDesktopView, isTabletView } = useWindowSize();
    const productsByQueryEndWithParams = useMemo(() => `${productsByQueryEnd}${encodeURIComponent('productType.id:')}"${productTypeId}"`, [productTypeId, productsByQueryEnd]);
    const onChangeHandler = useCallback((value) => {
        setSearchText(value);
        if (value.length > MIN_SEARCH_LENGTH) {
            setIsLoading(true);
            setDigitalData(DigitalDataMutation.COMPATIBILITY_SEARCH_PRODUCT, {
                term: value,
            });
            getProductByName(productsByQueryEndWithParams, value, locale).then((products) => {
                setIsLoading(false);
                setProductResult(products);
                setProductOffsetParam(0);
                setSelectModelScrollHeight(0);
            });
        }
    }, [locale, productsByQueryEndWithParams]);
    const onClearHandler = useCallback(() => {
        setSearchText('');
        setProductOffsetParam(0);
        setSelectModelScrollHeight(0);
        setProductResult([]);
        setSelectedProduct(null);
        setCurrentProductResult(null);
    }, [setSelectedProduct]);
    const onSelectHandler = useCallback((value) => {
        const currentProduct = findProductByKey(productResult, value.value);
        setSearchText(value.label);
        setCurrentProductResult(value);
        setSelectedProduct(currentProduct);
        if (currentProduct) {
            setDigitalData(DigitalDataMutation.COMPATIBILITY_SELECT_PRODUCT, {
                term: searchText,
                productId: currentProduct?.slug,
            });
        }
    }, [productResult, searchText, setSelectedProduct]);
    const onScrollSelectModelDropdown = useCallback((scrollRef) => {
        const scrollCurrent = scrollRef.current;
        if (scrollCurrent) {
            const isEndOfScroll = getIsEndOfScroll(scrollCurrent);
            const scrollHeight = scrollCurrent.scrollTop;
            if (isEndOfScroll && scrollHeight > selectModelScrollHeight) {
                setSelectModelScrollHeight(scrollHeight);
                setProductOffsetParam((prev) => prev + 10);
            }
        }
    }, [selectModelScrollHeight]);
    const onBlurHandler = useCallback(() => {
        if (productResult?.length) {
            setProductOffsetParam(0);
            setSelectModelScrollHeight(0);
            setProductResult([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const getDropdownMaxHeight = useCallback(() => {
        if (isInfiniteDesktopView) {
            return DropdownListMaxHeight.DESKTOP;
        }
        if (isTabletView) {
            return DropdownListMaxHeight.TABLET;
        }
        return DropdownListMaxHeight.MOBILE;
    }, [isInfiniteDesktopView, isTabletView]);
    useEffect(() => {
        if (productOffsetParam) {
            setIsLoading(true);
            getProductByName(productsByQueryEndWithParams, searchText, locale, productOffsetParam)
                .then((products) => {
                setProductResult((prev) => [...prev, ...products]);
                setIsLoading(false);
            })
                .catch(() => setIsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productOffsetParam]);
    return (React.createElement(AutocompleteRequest, { onChange: onChangeHandler, label: "" // TODO: (D.K. & Y.R.) Move into labels dictionary (i18n)
        , useForMobile: !isInfiniteDesktopView, icon: Search, value: searchText, placeholder: get(labels, 'searchNameInputPlaceholder', ''), noResultTitle: get(labels, 'searchInputNoResultsHeader'), noResultDescription: get(labels, 'searchInputNoResultsDescription'), resultsListRangeOfMaxVisibleItems: resultsListRangeOfMaxVisibleItems, isLoading: isLoading, inputImage: currentProductResult?.image, dropdownMaxHeightValue: getDropdownMaxHeight(), minCharacters: 2, suggestionList: prepareProductsInRightFormat(productResult), infoText: "", isItemWithImage: true, hasError: false, isRightToLeft: isRightToLeft, onScroll: onScrollSelectModelDropdown, onClear: onClearHandler, onSelect: onSelectHandler, onBlur: onBlurHandler, isFocused: true }));
}
